<template>
  <div>
    <div
      class="field"
      :class="{
        'field--vertical': !horizontal,
        'field--centered': centered,
        'field--skeleton': skeletonMode,
      }"
    >
      <label :for="label" class="field__label" :class="noLabel ? 'no-label' : ''">
        <span v-if="!skeletonMode">
          {{ label }}
          <span v-if="iconAction" class="field__label-action" @click="() => $emit('clickAction')">
            <i class="mdi" :class="`mdi-${iconAction}`"></i>
          </span>
        </span>
        <span v-if="required && !disabled && label" class="field__label-required">*</span>
        <Tip v-if="tooltipText" :text="tooltipText" :align="tooltipAlign" class="tooltip-field" />
      </label>
      <validation-provider
        :rules="rules"
        :name="label || name"
        v-slot="{ errors }"
        ref="validator"
        :vid="vid"
      >
        <div class="field__control" :class="{ 'is-invalid': errors.length > 0 }">
          <slot v-if="!skeletonMode" />
          <span v-if="errors.length > 0" class="field__errors">
            <i class="mdi mdi-alert-circle" /> {{ errors[0] }}
          </span>
        </div>
      </validation-provider>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    label: {
      type: String,
    },
    horizontal: {
      type: Boolean,
      default: true,
    },
    centered: {
      type: Boolean,
      default: false,
    },
    skeletonMode: {
      type: Boolean,
      default: false,
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
    tooltipText: String,
    tooltipAlign: { type: String, default: 'right' },
    iconAction: {
      type: String,
      default: '',
    },
  },
  name: 'Field',
  data() {
    return {
      disabled: false,
      name: '',
      required: false,
      rules: '',
      vid: '',
    };
  },
  mounted() {
    this.$refs.validator.$children.forEach((x) => {
      if (x.$attrs.vid) {
        this.vid = x.$attrs.vid;
      }
      if (x.$attrs.name || x.name) {
        this.name = x.$attrs.name || x.name;
      }

      if (x.$attrs.required || x.required) {
        this.required = x.$attrs.required || x.required;
      }
      if (x.$attrs.rules) {
        this.rules = x.$attrs.rules;
      }
    });

    this.$watch(
      '$refs.validator.$children.0.$attrs.rules',
      function (val) {
        this.rules = val;
      },
      { deep: true }
    );

    this.$root.$on('changeModeFrame', (mode) => {
      this.disabled = mode === 'view';
    });
  },
  computed: {
    hasErrors() {
      return (this.errors || []).length;
    },
  },
  methods: {
    changeName(name) {
      this.name = name;
    },
    changeRequired(required) {
      this.required = required;
    },
  },
  watch: {
    disabled(val) {
      this.$children.forEach((vNode) => {
        let containDisabled = false;
        Object.keys(vNode.$props).forEach((x) => {
          if (x === 'disabled') {
            vNode.$emit('update:disabled', val);
            containDisabled = true;
          }
        });
        if (!containDisabled) {
          this.$set(vNode, 'disabled', val);
        }
      });
    },
  },
};
</script>

<style lang="scss">
@keyframes pulse {
  0% {
    background-color: rgba(165, 165, 165, 0.1);
  }
  50% {
    background-color: rgba(165, 165, 165, 0.3);
  }
  100% {
    background-color: rgba(165, 165, 165, 0.1);
  }
}
@keyframes skeletonPulse {
  0% {
    background-color: rgba(0, 0, 0, 0.1);
  }
  50% {
    background-color: rgba(0, 0, 0, 0.15);
  }
  100% {
    background-color: rgba(0, 0, 0, 0.1);
  }
}
@media (max-width: 667px) {
  .tooltip-field .pw-tooltip__text {
    transform: translate(-102%, 25%) !important;
  }
  .tooltip-field .pw-tooltip__text::after {
    top: -10% !important;
    right: 5% !important;
    border-color: transparent transparent #edf1f3 transparent !important;
  }
}
.field {
  margin-bottom: 20px;
  &__control {
    &:focus {
      outline: 0 !important;
    }
  }
  &--skeleton {
    .field__label {
      background-color: rgba(0, 0, 0, 0.1);
      width: 25%;
      height: 16px;
      line-height: 22px;
      border-radius: 3px;
      color: transparent !important;
      position: relative;
      overflow: hidden;
      &:after {
        position: absolute;
        content: '';
        display: block;
        width: 100%;
        height: 100%;

        animation: skeletonPulse 1s infinite;
      }
    }
    .field__control {
      background-color: rgba(0, 0, 0, 0.1);
      width: 100%;
      height: 32px;
      border-radius: 3px;
      position: relative;
      overflow: hidden;
      .pw-check-box {
        margin-top: 30px;
      }
      &:focus {
        outline: 0 !important;
      }
      &:after {
        position: absolute;
        content: '';
        display: block;
        width: 100%;
        height: 100%;

        animation: skeletonPulse 1s infinite;
      }
    }
  }
  &--centered {
    .field__label {
      text-align: center;
    }
    .field__control {
      text-align: center;
    }
  }
  .field__label {
    &.no-label {
      min-height: 22px;
    }
    &-action {
      color: #3d65c0;
      cursor: pointer;
    }
    margin-bottom: 5px;
    line-height: 22px;
    display: block;
    color: #2d3138;
    font-weight: 600;
    &-required {
      color: #f25555;
    }
  }
  &__errors {
    color: #f25555;
    font-size: 12px;
    line-height: 16px;
    margin-top: 3px;
    span {
      white-space: pre;
    }
  }
}

.is-invalid {
  .vue-treeselect__control,
  .upload,
  input {
    border-color: #f25555 !important;
  }

  .errors {
    color: #f25555 !important;
    font-size: 10px;
  }
}
</style>
